// In-house replacements for the numeral formats this app used.
// Output is pinned to numeral@2.0.6 behavior by numberFormatters.test.ts.

const toNumber = (value: number | string): number => {
  const parsed =
    typeof value === 'number'
      ? value
      : Number(String(value).replace(/[$,\s]/g, ''));

  return Number.isFinite(parsed) ? parsed : 0;
};

// numeral's rounding: shift the decimal representation, Math.round, shift
// back. Rounds what the user would read (0.0285 -> "0.029") instead of the
// binary double, and rounds negatives half toward +Infinity. A -0 result
// prints unsigned via toFixed, so "-0" never appears.
const roundFixed = (value: number, decimals: number): string =>
  (
    Math.round(Number(`${value}e+${decimals}`)) / Math.pow(10, decimals)
  ).toFixed(decimals);

const groupThousands = (rounded: string): string => {
  const negative = rounded.startsWith('-');
  const [int, decimal] = (negative ? rounded.slice(1) : rounded).split('.');
  const grouped = int.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');

  return (negative ? '-' : '') + grouped + (decimal ? `.${decimal}` : '');
};

// 1234567 -> "1,234,567"
export const formatInteger = (value: number | string): string =>
  groupThousands(roundFixed(toNumber(value), 0));

// 10.5 -> "10.50", 1234.56 -> "1,234.56", 10 -> "10.00"
export const formatMoneyWithCents = (value: number | string): string =>
  groupThousands(roundFixed(toNumber(value), 2));

// Like formatMoneyWithCents, but whole amounts drop the ".00":
// 10.5 -> "10.50", 10 -> "10". No currency symbol; callers prepend "$".
export const formatMoney = (value: number | string): string => {
  const withCents = formatMoneyWithCents(value);

  return withCents.endsWith('.00') ? withCents.slice(0, -3) : withCents;
};

// Two decimals plus a significant third: 0.0285 -> "0.029", 0.01 -> "0.01"
export const formatPerCreditRate = (value: number | string): string => {
  const parsed = toNumber(value);
  const decimalDigits = String(parsed).split('.')[1];
  const decimals = decimalDigits
    ? Math.min(Math.max(decimalDigits.length, 2), 3)
    : 2;
  const rounded = roundFixed(parsed, decimals);

  return decimals === 3 && rounded.endsWith('0')
    ? rounded.slice(0, -1)
    : rounded;
};

// 0.123 -> "12.3%" (multiplies by 100)
export const formatPercent = (value: number | string): string =>
  `${roundFixed(toNumber(value) * 100, 1)}%`;
